import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-02-09 14:54:18 +0100 (Mon, 09 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160486 $
//  Purpose   : react components for auth system
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState } from 'react';
import { Button, Step, Segment, Grid, Table, Icon, Menu, Card, Checkbox, Form, Message } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { VIcon } from 'uix/graphics';
import log from 'uix/logging';
import { get, isNil, strip, size } from 'ldash';
import connect from 'uix/connect';
import { useActions, useSelector, useUserSetting, useDebounce, useNotify } from 'uix/hooks';
import { applyProps } from 'uix/components/utils';
import { DebugFeature, XPopup, Dialog } from 'uix/components';
import { formatDateTime } from 'uix/utils';
// ---Local imports
import { beginWebauthn, loginWebauthn, queryWebauthn } from './actions';
import { useUserData } from './hooks';
import { checkAcl } from './acl';
import { XMenu } from '../components';
// ---Globals
let REGISTRATION_STEPS = [
    // step 1 - initialize registration options
    {
        label: 'Create Options',
        active: 'REG_BEGIN',
        completed: [
            'REG_OPTS',
            'REG_OK'
        ],
        failure: [
            'ERR_REG_BEGIN'
        ]
    },
    // step 2 - create and verify credentials
    {
        label: 'Verify Registration',
        active: 'REG_OPTS',
        completed: [
            'REG_OK'
        ],
        failure: [
            'ERR_REG_CREATE',
            'ERR_REG_VERIFY'
        ]
    }
];
let LOGIN_STEPS = [
    // step 1 - initialize login options
    {
        label: 'Create Options',
        active: 'LOGIN_BEGIN',
        completed: [
            'LOGIN_OPTS',
            'LOGIN_OK'
        ],
        failure: [
            'ERR_LOGIN_BEGIN'
        ]
    },
    // step 2 - create and verify credentials
    {
        label: 'Verify Credentials',
        active: 'LOGIN_OPTS',
        completed: [
            'LOGIN_OK'
        ],
        failure: [
            'ERR_LOGIN_CREATE',
            'ERR_LOGIN_VERIFY'
        ]
    }
];
const matchesFailureState = (state, failures)=>{
    if (!state) {
        return false;
    }
    return failures.some((f)=>state == f || state.startsWith(`${f}_`));
};
export const WebAuthNDiag = connect('WebAuthN', {
    storeMap: [
        'meta.user',
        'meta.webauthn'
    ],
    actionMap: {
        beginWebauthn,
        loginWebauthn,
        queryWebauthn
    }
}, ({ user, beginWebauthn, loginWebauthn, queryWebauthn, webauthn, callApi })=>{
    useEffect(()=>{
        if (isNil(webauthn.available)) {
            queryWebauthn(user.name);
        }
    }, []);
    let [keys, setKeys] = useState([]);
    useEffect(()=>{
        callApi('webauthn/keys', (data, error)=>setKeys(data && data.keys || []));
    }, [
        webauthn.state,
        webauthn.available
    ]);
    let key_id = (k)=>k.credential_id || k.cred_id;
    let show_dt = (dt)=>dt ? formatDateTime(dt) : '-';
    return /*#__PURE__*/ _jsxs(Grid, {
        columns: 2,
        children: [
            /*#__PURE__*/ _jsxs(Grid.Column, {
                width: 6,
                children: [
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "huge",
                                onClick: ()=>beginWebauthn(user),
                                content: "Register"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "huge",
                                onClick: ()=>beginWebauthn(user, {
                                        reset: true
                                    }),
                                content: "Reset"
                            }),
                            /*#__PURE__*/ _jsx(Step.Group, {
                                ordered: true,
                                size: "tiny",
                                children: REGISTRATION_STEPS.map((r, i)=>/*#__PURE__*/ _jsx(Step, {
                                        completed: r.completed.includes(webauthn.state),
                                        content: r.label,
                                        active: webauthn.state == r.active,
                                        className: matchesFailureState(webauthn.state, r.failure) ? 'negative' : null
                                    }, i))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                disabled: !Boolean(webauthn.available),
                                size: "huge",
                                onClick: ()=>loginWebauthn(user.name),
                                content: `Login with ${user.name}`
                            }),
                            /*#__PURE__*/ _jsx(Step.Group, {
                                ordered: true,
                                size: "tiny",
                                children: LOGIN_STEPS.map((r, i)=>/*#__PURE__*/ _jsx(Step, {
                                        completed: r.completed.includes(webauthn.state),
                                        content: r.label,
                                        active: webauthn.state == r.active,
                                        className: matchesFailureState(webauthn.state, r.failure) ? 'negative' : null
                                    }, i))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx("tt", {
                                children: webauthn.state
                            }),
                            webauthn.error ? /*#__PURE__*/ _jsxs("tt", {
                                children: [
                                    ' ',
                                    get(webauthn.error, 'errorMsg') || get(webauthn.error, 'message') || get(webauthn.error, 'name')
                                ]
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Grid.Column, {
                width: 10,
                children: /*#__PURE__*/ _jsxs(Table, {
                    definition: true,
                    children: [
                        /*#__PURE__*/ _jsx(Table.Header, {
                            children: /*#__PURE__*/ _jsxs(Table.Row, {
                                children: [
                                    /*#__PURE__*/ _jsx(Table.HeaderCell, {}),
                                    keys.map((k, i)=>/*#__PURE__*/ _jsx(Table.HeaderCell, {
                                            children: `Key ${i}`
                                        }, key_id(k)))
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Table.Body, {
                            children: [
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "ID"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: key_id(k).slice(0, 20)
                                            }, key_id(k)))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "Created"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: show_dt(k.created_dt)
                                            }, key_id(k)))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "Last Used"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: show_dt(k.last_used_dt)
                                            }, key_id(k)))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "Sign Count"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: k.sign_count || 0
                                            }, key_id(k)))
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
});
export const UserInfo = ({ style, puid, title, shortenInitials = false })=>{
    /* a user name with an icon
   */ puid = parseInt(puid);
    let curr_user = useSelector(Sel.user);
    let runImpersonify = useActions('runImpersonify');
    let user = useUserData(puid);
    let content = /*#__PURE__*/ _jsx(Icon, {
        style: style,
        name: "user outline",
        color: "grey",
        title: `puid: ${puid}`
    });
    if (user) {
        // non admin lookups emit only {puid, fullname}
        let name = user.fullname || user.login || '';
        if (shortenInitials) {
            let sep = name.includes('.') ? '.' : ' ';
            let initials = name.split(sep).map((n)=>strip(n)[0]).filter((e)=>e);
            if (!title) {
                title = name;
            }
            // no short name for server user type
            name = user.type != 10 ? initials.map((s)=>`${s}.`).join(' ') : '';
        }
        let buildIcon = (type)=>{
            if (type == 10) {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: "server",
                    color: "grey"
                });
            } else {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: "user",
                    color: "grey"
                });
            }
        };
        // handle debug user impersonify
        let onClick = null;
        let styles = {
            paddingLeft: '2px',
            ...style
        };
        if (user.login && curr_user.hasGroup('debug')) {
            onClick = ()=>runImpersonify(user);
            styles = {
                ...styles,
                cursor: 'pointer'
            };
        }
        return /*#__PURE__*/ _jsxs("span", {
            style: styles,
            title: title || `puid: ${puid}`,
            onClick: onClick,
            children: [
                buildIcon(user.type),
                /*#__PURE__*/ _jsx("span", {
                    children: name
                })
            ]
        });
    }
    return content;
};
export const LogoutMenuItem = ()=>{
    let runLogout = useActions(Actions.runLogout);
    return /*#__PURE__*/ _jsx(Menu.Item, {
        onClick: runLogout,
        title: "Logout",
        icon: "sign out"
    });
};
const ResetPartPersistence = ({})=>{
    let clearPartPersistence = useActions('clearPartPersistence');
    let request = useSelector(Sel.request);
    let classCfg = useSelector(Sel.classCfg);
    let parts = [
        ...classCfg.parts,
        ...classCfg.tabs[request.tabname].parts
    ];
    parts = parts.map((p, i)=>{
        let n = p.name || p.type;
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(Button, {
                fluid: true,
                size: "tiny",
                basic: true,
                compact: true,
                icon: "history",
                content: `Reset ${n}`,
                onClick: ()=>clearPartPersistence(n)
            })
        }, `${n}_${i}`);
    });
    return /*#__PURE__*/ _jsx("div", {
        children: parts
    });
};
const UserAccountSettings = ()=>{
    let user = useSelector(Sel.user);
    let userData = useUserData(user.puid);
    let domain = useSelector(Sel.domain);
    let webauthn = useSelector((root)=>root.meta.webauthn);
    let callApi = useActions('callApi');
    let notify = useNotify();
    let setUserMeta = useActions('setUser');
    let runWebauthnRegistration = useActions(beginWebauthn);
    let canEditUser = checkAcl('g:manageUsers', user, get(domain, 'name'));
    let [profile, setProfile] = useState({
        fullname: userData?.fullname || user.fullname || '',
        email: userData?.email || user.email || ''
    });
    useEffect(()=>{
        setProfile({
            fullname: userData?.fullname || user.fullname || '',
            email: userData?.email || user.email || ''
        });
    }, [
        user.puid,
        userData?.fullname,
        userData?.email
    ]);
    let [pw, setPw] = useState({
        f1: '',
        f2: ''
    });
    let [pwValidByPolicy, setPwValidByPolicy] = useState(false);
    let [dpw1] = useDebounce(pw.f1, 300);
    useEffect(()=>{
        if (!dpw1) {
            setPwValidByPolicy(false);
            return;
        }
        callApi('user/validatePassword', {
            password: dpw1
        }, (data)=>{
            setPwValidByPolicy(data?.result === 'success');
        });
    }, [
        dpw1
    ]);
    let pwMismatch = pw.f1 && pw.f1 != pw.f2;
    let pwReady = pw.f1 && pw.f2 && !pwMismatch && pwValidByPolicy;
    let saveProfile = ()=>callApi('user/update', {
            puid: user.puid,
            fullname: profile.fullname,
            email: profile.email
        }, (data, error)=>{
            if (error) {
                notify.error({
                    error
                });
                return;
            }
            notify.success({
                message: 'Personal info updated.'
            });
        });
    let changePassword = ()=>callApi('user/update', {
            puid: user.puid,
            passwd: pw.f1
        }, (data, error)=>{
            if (error) {
                notify.error({
                    error
                });
                return;
            }
            setPw({
                f1: '',
                f2: ''
            });
            notify.success({
                message: 'Password changed.'
            });
        });
    let resetPasskey = ()=>{
        runWebauthnRegistration(user, {
            reset: true
        });
    };
    return /*#__PURE__*/ _jsxs(Form, {
        size: "small",
        children: [
            !canEditUser && /*#__PURE__*/ _jsxs(Message, {
                size: "tiny",
                warning: true,
                style: {
                    marginBottom: '0.5rem'
                },
                children: [
                    "Personal info and password change currently require ",
                    /*#__PURE__*/ _jsx("tt", {
                        children: "manageUsers"
                    }),
                    " permission."
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Input, {
                disabled: !canEditUser,
                label: "Name",
                value: profile.fullname,
                onChange: (e, data)=>setProfile({
                        ...profile,
                        fullname: data.value
                    }),
                style: {
                    marginBottom: '0.35rem'
                }
            }),
            /*#__PURE__*/ _jsx(Form.Input, {
                disabled: !canEditUser,
                label: "E-Mail",
                value: profile.email,
                onChange: (e, data)=>setProfile({
                        ...profile,
                        email: data.value
                    }),
                style: {
                    marginBottom: '0.35rem'
                }
            }),
            /*#__PURE__*/ _jsx(Button, {
                disabled: !canEditUser,
                type: "button",
                size: "tiny",
                basic: true,
                icon: "save",
                content: "Save",
                onClick: saveProfile
            }),
            /*#__PURE__*/ _jsx(Form.Input, {
                disabled: !canEditUser,
                type: "password",
                label: "Neues Passwort",
                autoComplete: "new-password",
                value: pw.f1,
                onChange: (e, data)=>setPw({
                        ...pw,
                        f1: data.value
                    }),
                style: {
                    marginTop: '0.35rem',
                    marginBottom: '0.35rem'
                }
            }),
            /*#__PURE__*/ _jsx(Form.Input, {
                disabled: !canEditUser,
                type: "password",
                label: "Passwort wiederholen",
                autoComplete: "new-password",
                value: pw.f2,
                onChange: (e, data)=>setPw({
                        ...pw,
                        f2: data.value
                    }),
                error: pwMismatch ? 'Passwort stimmt nicht überein.' : null,
                style: {
                    marginBottom: '0.35rem'
                }
            }),
            /*#__PURE__*/ _jsx(Button, {
                disabled: !canEditUser || !pwReady,
                type: "button",
                size: "tiny",
                basic: true,
                icon: "key",
                content: "Change Password",
                onClick: changePassword
            }),
            /*#__PURE__*/ _jsx(Button, {
                type: "button",
                size: "tiny",
                basic: true,
                icon: "redo",
                content: "Reset Passkey",
                onClick: resetPasskey
            }),
            !!webauthn?.state && /*#__PURE__*/ _jsx("div", {
                style: {
                    marginTop: '0.15rem',
                    fontSize: '0.8em'
                },
                children: /*#__PURE__*/ _jsx("tt", {
                    children: webauthn.state
                })
            })
        ]
    });
};
const GlobalUserSettings = ()=>{
    /* custom user settings
  using persistence via store.root.ui.byId['Global/*']
  */ let clearLocalStorage = useActions(Actions.clearLocalStorage);
    let switchTheme = useActions(Actions.switchTheme);
    let theme = useSelector((root)=>root.meta.theme.name);
    let [colorizeDomains, setColorizeDomains] = useUserSetting('colorizeDomains');
    let [headerStyle, setHeaderStyle] = useUserSetting('headerStyle');
    let [navBarTarget, setNavBarTarget] = useUserSetting('navBarTarget');
    const NavBarButtons = ()=>/*#__PURE__*/ _jsxs("div", {
            style: {
                paddingBottom: '0.5rem'
            },
            children: [
                /*#__PURE__*/ _jsxs(Button.Group, {
                    style: {
                        paddingRight: '0.5rem'
                    },
                    size: "mini",
                    label: "position",
                    labeled: true,
                    color: "grey",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'leftPortal',
                            onClick: ()=>setNavBarTarget('leftPortal'),
                            icon: "chevron left"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'topPortal',
                            onClick: ()=>setNavBarTarget('topPortal'),
                            icon: "chevron up"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerLeftPortal',
                            onClick: ()=>setNavBarTarget('centerLeftPortal'),
                            icon: "circle outline"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerBottomPortal',
                            onClick: ()=>setNavBarTarget('centerBottomPortal'),
                            icon: "chevron down"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerRightPortal',
                            onClick: ()=>setNavBarTarget('centerRightPortal'),
                            icon: "chevron right"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        fontSize: 'var(--vl_bodySecondary_fontSize)'
                    },
                    children: "Position der Navigation"
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: colorizeDomains,
                label: "Colorize Domains",
                onClick: ()=>setColorizeDomains(!colorizeDomains)
            }),
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: headerStyle === 'compact',
                label: "Compact Header",
                onClick: ()=>setHeaderStyle(headerStyle === 'default' ? 'compact' : 'default')
            }),
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: theme === 'dark',
                label: "Dark Mode",
                onClick: ()=>switchTheme(theme === 'dark' ? 'default' : 'dark')
            }),
            /*#__PURE__*/ _jsx(AclFeature, {
                acl: "g:admin",
                children: /*#__PURE__*/ _jsx(NavBarButtons, {})
            }),
            /*#__PURE__*/ _jsx(Button, {
                color: "grey",
                icon: "history",
                size: "tiny",
                fluid: true,
                content: "Reset all Local Settings",
                onClick: clearLocalStorage
            }),
            /*#__PURE__*/ _jsx(ResetPartPersistence, {})
        ]
    });
};
export const ProfileMenuItem = ()=>{
    let [openPopup, setOpenPopup] = useState(false);
    let [settings, setSettings] = useState(false);
    let [accountSettings, setAccountSettings] = useState(false);
    let user = useSelector(Sel.user);
    let userData = useUserData(user.puid);
    let runLogout = useActions(Actions.runLogout);
    const settingHandle = ()=>{
        setSettings(!settings);
        setAccountSettings(false);
    };
    const accountHandle = ()=>{
        setAccountSettings(!accountSettings);
        setSettings(false);
    };
    if (!userData) {
        return /*#__PURE__*/ _jsx(Menu.Item, {
            icon: "user circle outline"
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(XPopup, {
            on: "click",
            basic: true,
            fitted: true,
            position: "bottom right",
            onOpen: ()=>setOpenPopup(!openPopup),
            onClose: ()=>setOpenPopup(!openPopup),
            trigger: /*#__PURE__*/ _jsx(Menu.Item, {
                title: "User Menü",
                icon: "user circle outline",
                active: openPopup
            }),
            children: /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            style: {
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                verticalAlign: 'middle',
                                justifyContent: 'space-between'
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    style: {
                                        padding: '0 0.5rem'
                                    },
                                    children: [
                                        user.fullname || user.name,
                                        /*#__PURE__*/ _jsx(Card.Meta, {
                                            children: userData.email
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(XMenu, {
                                    style: {
                                        margin: 0
                                    },
                                    secondary: true,
                                    children: [
                                        /*#__PURE__*/ _jsx(XMenu.Item, {
                                            active: settings,
                                            icon: "cog",
                                            onClick: settingHandle
                                        }),
                                        /*#__PURE__*/ _jsx(XMenu.Item, {
                                            active: accountSettings,
                                            icon: "edit",
                                            onClick: accountHandle
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    accountSettings && /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsx(UserAccountSettings, {})
                    }),
                    settings && /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsx(GlobalUserSettings, {})
                    }),
                    /*#__PURE__*/ _jsx(Card.Content, {
                        textAlign: "center",
                        extra: true,
                        children: /*#__PURE__*/ _jsxs(Button, {
                            size: "small",
                            animated: "fade",
                            onClick: runLogout,
                            children: [
                                /*#__PURE__*/ _jsx(Button.Content, {
                                    hidden: true,
                                    children: /*#__PURE__*/ _jsx(VIcon, {
                                        name: "sign-out"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button.Content, {
                                    visible: true,
                                    children: "Logout"
                                })
                            ]
                        })
                    })
                ]
            })
        })
    });
};
export const UserMenuItem = ()=>{
    let setContextHelpOn = useActions(Actions.setContextHelpOn);
    let contextHelpOn = useSelector((root)=>root.meta.contextHelp.on);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                onClick: ()=>setContextHelpOn(!contextHelpOn),
                icon: "question circle outline",
                title: "Hilfe",
                active: contextHelpOn
            }),
            /*#__PURE__*/ _jsx(ProfileMenuItem, {})
        ]
    });
};
export const AclFeature = ({ acl, fallback, children, ...props })=>{
    /* render children if acl check passes
   */ let user = useSelector(Sel.user);
    let { name: dname } = useSelector(Sel.domain);
    if (!checkAcl(acl, user, dname)) {
        if (fallback) {
            return fallback;
        }
        return null;
    }
    // a AclFeature in a XMenu needs props pass through
    return size(props) ? applyProps(children, props) : children;
};
